import React, { useEffect, useState } from 'react';
import './modernized_app.css';

const centsToDollars = value => {
  if (value === null || value === undefined || value === '') return '';
  return (Number(value) / 100).toFixed(2);
};

const formatCurrencyFromCents = value => {
  const dollars = centsToDollars(value);
  return dollars ? `$${dollars}` : '$0.00';
};

const formatMonthlyPrice = value => `${formatCurrencyFromCents(value)}/mo`;

export default function UpgradeTab({ api, user, selectedBase, billingReturn }) {
  const [billingCatalog, setBillingCatalog] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [billingCycle, setBillingCycle] = useState('annual');
  const [billingAction, setBillingAction] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  const location = selectedBase || user?.location || '';

  const loadPlans = async () => {
    if (!api || !user?.email || !location) return;
    try {
      setIsLoading(true);
      setError('');
      const res = await api.get('/billing/plans', {
        params: {
          email: user.email,
          location,
        },
      });
      setBillingCatalog(res.data.catalog || null);
      setSubscription(res.data.subscription || null);
    } catch (err) {
      setError(err.response?.data?.message || 'Unable to load plan options.');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadPlans();
  }, [api, user?.email, location]);

  useEffect(() => {
    if (!billingReturn?.status) return undefined;

    const planText = billingReturn.planKey ? ` for ${billingReturn.planKey}` : '';
    setError('');
    if (billingReturn.status === 'success') {
      setMessage(`Checkout complete${planText}. We are refreshing your subscription now; Stripe may take a few seconds to finish activating the plan.`);
      const quickRefresh = window.setTimeout(loadPlans, 800);
      const followUpRefresh = window.setTimeout(loadPlans, 3500);
      return () => {
        window.clearTimeout(quickRefresh);
        window.clearTimeout(followUpRefresh);
      };
    }
    if (billingReturn.status === 'cancelled') {
      setMessage('Checkout was canceled. No billing changes were made.');
      loadPlans();
      return undefined;
    }
    if (billingReturn.status === 'portal_return') {
      setMessage('Returned from the billing portal. We refreshed the latest subscription details.');
      loadPlans();
    }
    return undefined;
  }, [billingReturn?.status, billingReturn?.planKey]);

  const startBillingCheckout = async planKey => {
    if (!planKey) return;
    try {
      setBillingAction(planKey);
      setError('');
      setMessage('');
      const res = await api.post('/billing/create-checkout-session', {
        email: user.email,
        location,
        planKey,
      });
      if (res.data?.url) {
        window.location.href = res.data.url;
      } else {
        setError('Checkout did not return a billing link.');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Unable to start billing checkout.');
    } finally {
      setBillingAction('');
    }
  };

  const plans = billingCatalog?.plans || {};
  const standardPlan = plans.standard_monthly || null;
  const standardAnnualPlan = plans.standard_annual_promo || null;
  const selectedStandardPlan = billingCycle === 'annual' ? (standardAnnualPlan || standardPlan) : standardPlan;
  const premiumMonthlyPlan = plans.premium_monthly || null;
  const premiumAnnualPlan = plans.premium_annual_promo || null;
  const selectedPremiumPlan = billingCycle === 'annual' ? premiumAnnualPlan : premiumMonthlyPlan;
  const addOns = billingCatalog?.addOns || {};
  const visibleAddOns = Object.values(addOns).filter(addOn => addOn?.visible !== false);
  const currentPlanKey = subscription?.billingPlanKey || '';
  const stripeConfigured = Boolean(billingCatalog?.configured);

  const renderPlanCard = (plan, { premium = false, actionLabel = 'Start Plan' } = {}) => {
    if (!plan) return null;
    const isAnnual = plan.billingCadence === 'annual_prepaid';
    const isCurrent = currentPlanKey === plan.key;
    const isUnavailable = isAnnual && plan.promoActive === false;

    return (
      <article className={`admin-plan-card ${premium ? 'admin-plan-card--premium' : ''} ${isCurrent ? 'is-current' : ''}`}>
        <div className="admin-plan-card__top">
          <span>{premium ? 'Premium' : 'Standard'}</span>
          {isAnnual ? (
            <>
              <strong>
                <em>{formatMonthlyPrice(plan.regularMonthlyCents)}</em>
                {formatMonthlyPrice(plan.monthlyEquivalentCents)}
              </strong>
              <small>
                Annual prepay, billed {formatCurrencyFromCents(plan.priceCents)} for {plan.months || 12} months
              </small>
              {plan.discountPercent ? (
                <b className="admin-plan-card__discount">Limited-time {plan.discountPercent}% discount</b>
              ) : null}
            </>
          ) : (
            <>
              <strong>{formatMonthlyPrice(plan.monthlyEquivalentCents)}</strong>
              <small>No annual prepay required</small>
            </>
          )}
        </div>
        <ul>
          {(plan.features || []).map(feature => (
            <li key={feature}>{feature}</li>
          ))}
        </ul>
        {isCurrent ? (
          <button type="button" disabled>Current Plan</button>
        ) : (
          <button
            type="button"
            onClick={() => startBillingCheckout(plan.key)}
            disabled={!stripeConfigured || isUnavailable || billingAction === plan.key}
          >
            {billingAction === plan.key ? 'Opening...' : actionLabel}
          </button>
        )}
      </article>
    );
  };

  const renderAddOnCard = addOn => {
    if (!addOn) return null;
    const actionText = addOn.addOnType === 'seats'
      ? `Add ${addOn.seatCount || 25} Seats`
      : addOn.displayName ? `Add ${addOn.displayName}` : 'Add Pack';
    return (
      <article key={addOn.key} className="admin-plan-card upgrade-addon-card">
        <div className="admin-plan-card__top">
          <span>A La Carte Add-On</span>
          <strong>{formatMonthlyPrice(addOn.monthlyEquivalentCents)}</strong>
          <small>{addOn.displayName || 'Optional add-on'}</small>
        </div>
        <ul>
          {(addOn.features || []).map(feature => (
            <li key={feature}>{feature}</li>
          ))}
        </ul>
        <button
          type="button"
          onClick={() => startBillingCheckout(addOn.key)}
          disabled={!stripeConfigured || billingAction === addOn.key}
        >
          {billingAction === addOn.key ? 'Opening...' : actionText}
        </button>
      </article>
    );
  };

  return (
    <div className="upgrade-tab">
      <header className="admin-hero upgrade-hero">
        <div>
          <div className="reports-eyebrow">Subscription Plans</div>
          <h2>Upgrade FORGE</h2>
          <p>Compare Standard and Premium features for {location || 'your location'} and start checkout when your team is ready.</p>
        </div>
        <button type="button" className="admin-refresh-button" onClick={loadPlans} disabled={isLoading}>
          {isLoading ? 'Refreshing...' : 'Refresh Plans'}
        </button>
      </header>

      {message ? <div className="admin-message is-success">{message}</div> : null}
      {error ? <div className="admin-message is-error">{error}</div> : null}

      <section className="reports-panel admin-panel admin-plan-comparison-panel upgrade-plan-panel">
        <div className="admin-plan-comparison-header">
          <div>
            <h3>Choose a Plan</h3>
            <p>Standard covers core maintenance execution. Premium adds AI reports, Launch Success Advisor prediction, Weather dashboards, auto-recovery scheduling, and early access to advanced features.</p>
          </div>
          <div className="admin-billing-cycle-toggle" role="group" aria-label="Billing cadence">
            <button
              type="button"
              className={billingCycle === 'monthly' ? 'is-active' : ''}
              onClick={() => setBillingCycle('monthly')}
            >
              Monthly
            </button>
            <button
              type="button"
              className={billingCycle === 'annual' ? 'is-active' : ''}
              onClick={() => setBillingCycle('annual')}
            >
              Annual Prepay
            </button>
          </div>
        </div>

        {!stripeConfigured ? (
          <div className="admin-billing-note is-warning">
            Payment links are not active yet because Stripe checkout and webhooks are not fully configured on the backend.
          </div>
        ) : null}

        {subscription ? (
          <div className="admin-billing-note">
            Current location plan: <strong>{subscription.displayPlanName || subscription.planName || 'Standard'}</strong>
            {subscription.billingPlanKey ? ` (${subscription.billingPlanKey})` : ''}.
          </div>
        ) : null}

        <div className="admin-plan-card-grid">
          {renderPlanCard(selectedStandardPlan, { actionLabel: 'Start Standard' })}
          {renderPlanCard(selectedPremiumPlan, { premium: true, actionLabel: 'Upgrade To Premium' })}
        </div>

        {visibleAddOns.length > 0 ? (
          <div className="upgrade-addon-section">
            <div>
              <h3>A La Carte Add-Ons</h3>
              <p>Add capacity or future optional features without changing the main plan tier.</p>
            </div>
            <div className="admin-plan-card-grid admin-plan-card-grid--addon">
              {visibleAddOns.map(addOn => renderAddOnCard(addOn))}
            </div>
          </div>
        ) : null}

        <div className="upgrade-footnote">
          Enterprise customers, group purchases, wing-level purchases, or multi-location contracts should contact{' '}
          <a href="mailto:info@mfe.llc?subject=FORGE Enterprise or group purchase contracting">info@mfe.llc</a>{' '}
          for contracting and custom pricing.
        </div>
      </section>
    </div>
  );
}
